<script setup lang="ts">
// An encode's vertical picture (vertical.ts): rotated, or the input on a
// background with a graphic over it, with a sketch of where it goes.
import { computed } from 'vue'
import type { Media, Video } from '../../api'
import { pictureBox, verticalSizes } from '../../vertical'

const video = defineModel<Video>({ required: true })
const props = defineProps<{ media: Media[]; inW: number; inH: number }>()

const mode = computed({
    get: () => video.value.vertical?.mode ?? '',
    set: (m: '' | 'rotate' | 'composite') => {
        if (!m) {
            video.value.vertical = undefined
            video.value.width = video.value.height = undefined // a vertical size alone would stretch the picture
            return
        }
        video.value.vertical = { ...video.value.vertical, mode: m }
        if (!verticalSizes.includes(`${video.value.width}x${video.value.height}`))
            [video.value.width, video.value.height] = [1080, 1920]
    },
})
const vt = computed(() => video.value.vertical!)
const W = computed(() => video.value.width || 1080)
const H = computed(() => video.value.height || 1920)
const centred = computed({
    get: () => vt.value.top === undefined,
    set: (c: boolean) => (vt.value.top = c ? undefined : box.value.top),
})
const box = computed(() =>
    pictureBox(W.value, H.value, props.inW, props.inH, Number(vt.value.picture_width) || undefined, vt.value.top),
)
const backgrounds = computed(() => props.media.filter((m) => m.kind === 'image' || m.kind === 'video'))
const graphics = computed(() => props.media.filter((m) => m.kind === 'image'))
</script>

<template>
    <details :open="!!video.vertical" data-test="vertical">
        <summary>Vertical{{ video.vertical ? ` · ${video.vertical.mode}` : '' }}</summary>
        <div class="mb-2.5">
            <label>Vertical picture</label>
            <select v-model="mode" data-test="vertical-mode">
                <option value="">no: the input's own shape</option>
                <option value="rotate">rotate the input a quarter turn (a camera on its side)</option>
                <option value="composite">the input on a background, with a graphic over it</option>
            </select>
        </div>
        <template v-if="video.vertical?.mode === 'rotate'">
            <div class="mb-2.5">
                <label>Turn</label>
                <select v-model="vt.rotate">
                    <option :value="undefined">clockwise</option>
                    <option value="ccw">counter-clockwise</option>
                </select>
            </div>
            <p class="text-xs text-fg-3">
                Fitted to the {{ W }}×{{ H }} frame (the size above); black where it does not fill it.
            </p>
        </template>
        <div v-if="video.vertical?.mode === 'composite'" class="flex gap-3">
            <div class="min-w-0 flex-1">
                <div class="mb-2.5">
                    <label>Background</label>
                    <select v-model="vt.background_media_id" data-test="vertical-background">
                        <option :value="undefined">black</option>
                        <option v-for="m in backgrounds" :key="m.id" :value="m.id">
                            {{ m.kind === 'video' ? 'video' : 'picture' }}: {{ m.name }}
                        </option>
                    </select>
                </div>
                <div class="grid grid-cols-2 gap-2.5">
                    <div class="mb-2.5">
                        <label>Picture width (px)</label
                        ><input
                            v-model.number="vt.picture_width"
                            type="number"
                            min="2"
                            :max="W"
                            step="2"
                            :placeholder="String(W)"
                            data-test="vertical-width"
                        />
                    </div>
                    <div class="mb-2.5">
                        <label>Top (px)</label
                        ><input
                            v-model.number="vt.top"
                            type="number"
                            min="0"
                            :max="H - 1"
                            step="2"
                            :disabled="centred"
                            :placeholder="String(box.top)"
                            data-test="vertical-top"
                        />
                        <label class="mt-1 flex items-center gap-1.5 text-xs"
                            ><input v-model="centred" type="checkbox" class="w-auto" /> centred</label
                        >
                    </div>
                </div>
                <div class="mb-2.5">
                    <label>Graphic over it (PNG with transparency, {{ W }}×{{ H }})</label>
                    <select v-model="vt.overlay_media_id" data-test="vertical-graphic">
                        <option :value="undefined">none</option>
                        <option v-for="m in graphics" :key="m.id" :value="m.id">{{ m.name }}</option>
                    </select>
                </div>
            </div>
            <svg
                :viewBox="`0 0 ${W} ${H}`"
                class="w-20 shrink-0 self-start rounded-sm border border-line-strong"
                role="img"
                :aria-label="`the picture ${box.w}×${box.h} at ${box.top} px from the top`"
                data-test="vertical-sketch"
            >
                <rect :width="W" :height="H" class="fill-bg-3" />
                <rect :x="(W - box.w) / 2" :y="box.top" :width="box.w" :height="box.h" class="fill-accent opacity-70" />
                <rect
                    v-if="vt.overlay_media_id"
                    :width="W"
                    :height="H"
                    fill="none"
                    class="stroke-fg-2"
                    :stroke-width="W / 60"
                    :stroke-dasharray="`${W / 20} ${W / 30}`"
                />
            </svg>
        </div>
        <p v-if="video.vertical?.mode === 'composite'" class="text-xs text-fg-3">
            The background fills the frame (a video loops; its sound is not used). The input keeps its shape,
            {{ box.w }}×{{ box.h }}, centred across. Changing these restarts the encode. The media must be in this
            node's library.
        </p>
    </details>
</template>
